/**
 * Loading overlay with a spinner and an animated-dot status label.
 *
 * Two modes:
 *   - Full-screen (default): fixed-position overlay below `topOffset` px of
 *     header, used for app-init "Authenticating" / "Loading project" states.
 *   - Inline: absolute-position overlay inside a given `parent` element,
 *     used for in-panel data loads.
 *
 * Usage:
 *   const loader = new AppLoader();                                // full-screen
 *   const loader = new AppLoader({ topOffset: 0 });                // full viewport
 *   const loader = new AppLoader({ parent: containerEl,            // inline
 *                                  initialStatus: 'Loading prices' });
 *   loader.setStatus('Authenticating');
 *   loader.hide();
 */
export class AppLoader {
    constructor({ topOffset = 56, initialStatus = 'Loading', parent = null } = {}) {
        this._baseText = initialStatus;
        this._dotCount = 0;
        this._intervalId = null;
        this._el = null;
        this._textEl = null;
        this._topOffset = topOffset;
        this._parent = parent;
        this._inline = !!parent;

        this._render();
        this._startDotAnimation();
    }

    /** Update the status label (pass text without trailing dots). */
    setStatus(text) {
        this._baseText = text;
        this._dotCount = 0;
        this._updateText();
    }

    /** Remove the loader from the DOM. */
    hide() {
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        if (this._el) {
            this._el.remove();
            this._el = null;
        }
    }

    _startDotAnimation() {
        this._updateText();
        this._intervalId = setInterval(() => {
            this._dotCount = (this._dotCount + 1) % 4;
            this._updateText();
        }, 400);
    }

    _updateText() {
        if (this._textEl) {
            this._textEl.textContent = this._baseText + '.'.repeat(this._dotCount);
        }
    }

    _render() {
        // Inject the shared stylesheet once per document. Both full-screen
        // and inline instances reuse it.
        if (!document.getElementById('app-loader-styles')) {
            const style = document.createElement('style');
            style.id = 'app-loader-styles';
            style.textContent = `
                .app-loader {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    background: var(--bg-primary, var(--color-card, #1e2530));
                    z-index: 99;
                    gap: 16px;
                }
                .app-loader.app-loader-fullscreen {
                    position: fixed;
                    left: 0;
                    right: 0;
                    bottom: 0;
                }
                .app-loader.app-loader-inline {
                    position: absolute;
                    inset: 0;
                }
                .app-loader-spinner {
                    width: 48px;
                    height: 48px;
                    border: 4px solid var(--border, var(--color-border, #2d3748));
                    border-top-color: var(--primary, var(--color-primary, #a0051b));
                    border-radius: 50%;
                    animation: app-loader-spin 1s linear infinite;
                }
                .app-loader p {
                    color: var(--text-muted, var(--color-text-muted, #9ca3af));
                    font-size: 14px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    min-width: 140px;
                    text-align: center;
                    margin: 0;
                }
                @keyframes app-loader-spin {
                    to { transform: rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
        }

        const el = document.createElement('div');
        el.className = this._inline ? 'app-loader app-loader-inline' : 'app-loader app-loader-fullscreen';
        if (!this._inline) {
            el.style.top = `${this._topOffset}px`;
        }
        el.innerHTML = '<div class="app-loader-spinner"></div><p></p>';

        this._el = el;
        this._textEl = el.querySelector('p');

        if (this._inline) {
            // Ensure parent can host an absolute-positioned child.
            const computed = getComputedStyle(this._parent).position;
            if (computed === 'static') this._parent.style.position = 'relative';
            this._parent.appendChild(el);
        } else {
            document.body.prepend(el);
        }
    }
}
